import { APP_SEARCH_FAILED_MESSAGE } from '@app-state/app.const';
import { Injectable, Injector, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Airline, Country, GetFlightDateOptionsResponse, Hotel, TourPackage } from '@appTypes/api.types';
import { TranslateService } from '@ngx-translate/core';
import { DistrictsTreeItem, ITourPackage } from '@appTypes/redefined-types';
import { deepClean, hotelPackageExtraParamsProcess } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { emptySearchResults } from '@shared/components/providers-unavailable/empty-search-results';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { isEmpty } from 'lodash-es';
import { errorResponseWithTrace } from '@core/interceptors/api.interceptor';
import { EMPTY, catchError, switchMap, tap, throwError, timer } from 'rxjs';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { ToursApiService } from '../services/tours-api.service';
import { SearchTourPackagesPaging, TourPackageResultsResponse } from '../tours.types';
import { TourChecksModel, TourRowCheckView, tourRowKey } from '../tours-row-checks.types';
import { withCheckedPackages, withRowCheck } from '../tours-row-checks.utils';
import {
  toursDismissTourRow,
  toursGetArrivalDistricts,
  toursGetAvailableAirlines,
  toursGetDestinationCountries,
  toursGetHotels,
  toursLoadFlightDateOptions,
  toursPollSearchProgress,
  toursReset,
  toursResetSearchResult,
  toursRowCheckAnswered,
  toursRowCheckStarted,
  toursSearchChildPackages,
  toursSearchTourPackages,
  toursShowRowAnyway
} from './tours.actions';
import { ToursFlightAvailabilityModel, ToursSearchFormDataModel, ToursStateModel } from './tours.model';
import { appRefreshCurrencyData } from '@app-state/app.actions';
import { airlineLogoIcon } from '@core/services/airline-logos.service';
import { noResultMessage, tourRefusalMessage } from '../utils/tour-search-refusal';

const defaults = new ToursStateModel();
/**
 * "Data not found" with the suppliers' rows kept beside it (run 24, Levan via agent B): an empty search is exactly when the supplier summary must
 * show which supplier failed or answered nothing.
 */
const emptyWithDiagnostics = (
  providers: string[] | null | undefined,
  diagnostics: TourPackageResultsResponse['diagnostics'],
  noResult?: TourPackageResultsResponse['noResult']
) => {
  // R-NR4: the refusal behind the empty list (no insurance, airports without a district ...) replaces "Data not found"
  // R-NR6: the search's trace id rides with the reason, so the box shows its Trace ID
  const empty = emptySearchResults<TourPackageResultsResponse>(providers, noResultMessage(noResult), noResult?.traceId);
  if (diagnostics) empty.loadState.additionlData = { ...empty.loadState.additionlData, diagnostics };
  return empty;
};
/** The text of a failed tour search: the page's own text of a known refusal code (R-NR4), else the server's text, else the generic failure. */
const searchErrorMessage = (
  e: { errorCode?: string; displayErrorMessage?: string; error?: { errorCode?: string } } | null | undefined
) =>
  tourRefusalMessage(e?.errorCode ?? e?.error?.errorCode, e?.displayErrorMessage) ??
  e?.displayErrorMessage ??
  APP_SEARCH_FAILED_MESSAGE;
/** Progressive tour search R-PT2: poll pace when the answer names none, and failed polls in a row before the page keeps what it has. */
const PROGRESS_POLL_DEFAULT_MS = 1500;
const PROGRESS_POLL_MAX_FAILURES = 3;

@State<ToursStateModel>({
  name: 'tours',
  defaults
})
@Injectable()
export class ToursState {
  readonly #catalogueApi = inject(CatalogueApiService);
  readonly #api = inject(ToursApiService);
  readonly #store = inject(Store);
  readonly #injector = inject(Injector);
  /** R-P2-15: an expired result repeats the search once; the repeat's own expiry does not repeat it again */
  #repeatedOnce = false;

  @Action(toursReset)
  toursReset({ setState }: StateContext<ToursStateModel>) {
    setState(defaults);
  }
  @Action(toursSearchTourPackages)
  toursSearchTourPackages(
    { getState, patchState, setState, dispatch }: StateContext<ToursStateModel>,
    { paging, repeatOfExpired }: toursSearchTourPackages
  ) {
    const {
      searchResults: { loadState },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;

    const payload = deepClean(searchForm.model);

    if (isEmpty(payload)) return;
    if (!repeatOfExpired) this.#repeatedOnce = false;
    if (paging !== undefined) {
      const pagingRecords = getState().searchResults.response?.pagingRecords;
      if (pagingRecords !== undefined) paging = { ...pagingRecords, ...paging };
    }
    // website-web#47: only the first answer of a search carries the diagnostics; a later page keeps the ones already shown
    const earlierDiagnostics = paging?.pagingId ? getState().searchResults.response?.diagnostics : undefined;
    if (!paging?.pagingId) {
      patchState({
        searchResults: new RequestStateModel<TourPackageResultsResponse>(new DataLoadStatusModel('loading')),
        childResults: [] // reset
      });
    } else
      setState(
        patch<ToursStateModel>({
          searchResults: patch<RequestStateModel<TourPackageResultsResponse>>({
            loadState: new DataLoadStatusModel('loading')
          })
        })
      );
    const pageRowCount = paging?.pageRowCount ?? 25;
    const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
    dispatch(new appRefreshCurrencyData()); // rates every search, the currency list if it never loaded
    return this.#api
      .searchTourPackages({
        ...payload,
        ...paging,
        pageRowCount: pageRowCount,
        searchCurrency,
        // online-api#37: the page answers from the stored result at once; no row is checked until the agent presses its price (Details)
        backgroundCheck: true,
        // R-PT1: results while the suppliers answer (online-api ignores it where switched off, and for a grouped search)
        progressive: !payload?.groupByHotel && !payload?.groupByHotelId
      })
      .pipe(
        tap((response) => {
          patchState({
            searchResults:
              // R-PT2: no tour yet while suppliers are still being asked is not "Data not found"
              !paging?.pagingId && !response.packages?.length && response.searchComplete !== false
                ? emptyWithDiagnostics(response.providersUnavailable, response.diagnostics, response.noResult)
                : new RequestStateModel<TourPackageResultsResponse>(
                    this.#resultsOf(response, undefined, paging, payload, response.diagnostics ?? earlierDiagnostics)
                  )
          });
          if (response.searchComplete === false && response.pagingId)
            dispatch(new toursPollSearchProgress(response.pagingId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS));
        }),
        catchError((e) => {
          if (!paging?.pagingId)
            patchState({
              searchResults: new RequestStateModel<TourPackageResultsResponse>(
                new DataLoadStatusModel('error', searchErrorMessage(e), errorResponseWithTrace(e))
              )
            });
          else
            setState(
              patch<ToursStateModel>({
                searchResults: patch<RequestStateModel<TourPackageResultsResponse>>({
                  loadState: new DataLoadStatusModel('error', searchErrorMessage(e), errorResponseWithTrace(e))
                })
              })
            );
          return throwError(() => e);
        })
      );
  }

  /**
   * The result model of an answer: rows with the hotel's extra params for the searched children, the paging records and the checks of the rows
   * the agent pressed (a changed row shows its checked package).
   */
  #resultsOf(
    response: TourPackageResultsResponse,
    checks: TourChecksModel | undefined,
    paging: Partial<SearchTourPackagesPaging> | undefined,
    payload: { groupByHotel?: unknown; groupByHotelId?: unknown; childrenAges?: number[] | null },
    diagnostics: TourPackageResultsResponse['diagnostics']
  ): TourPackageResultsResponse {
    return {
      ...response,
      checks,
      diagnostics,
      pagingRecords: {
        ...paging,
        pagingId: response.pagingId,
        pageNumber: response.pageNumber,
        pageCount: response.pageCount,
        totalCount: response.totalCount
      },
      isGroupByHotels: Boolean(payload?.groupByHotel ?? payload?.groupByHotelId),
      packages: (response.packages && withCheckedPackages(response.packages, checks))?.map((e) => ({
        ...e,
        hotel: e.hotel
          ? hotelPackageExtraParamsProcess({ childrenAges: payload.childrenAges ?? [] }, [e.hotel])[0]
          : undefined
      }))
    };
  }

  /**
   * Progressive tour search R-PT2 .. R-PT5 (docs features/website/tour-search-progressive.md): asks the running search for the page shown. While it
   * runs, page 1 takes the new rows (online-api merges every stage by price), a later page keeps its rows and takes the counts. The answer that
   * completes the search is a classic page of the stored result (its own paging id): it replaces the list, the rows the agent pressed keep their
   * checks. Stops when another search, page or reset replaced it, or after PROGRESS_POLL_MAX_FAILURES failed polls in a row.
   */
  @Action(toursPollSearchProgress, { cancelUncompleted: true })
  toursPollSearchProgress(
    { getState, setState, patchState, dispatch }: StateContext<ToursStateModel>,
    { pagingId, delayMs, failures }: toursPollSearchProgress
  ) {
    const isCurrent = () => {
      const response = getState().searchResults.response;
      return response?.pagingRecords?.pagingId === pagingId && response.searchComplete === false;
    };
    return timer(Math.max(0, delayMs)).pipe(
      switchMap(() => {
        const { searchResults, searchForm } = getState();
        const records = searchResults.response?.pagingRecords;
        if (!isCurrent() || !records || !searchForm.model || searchResults.loadState.status === 'loading') return EMPTY;
        const payload = deepClean(searchForm.model);
        const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
        const paging = {
          pagingId,
          pageNumber: records.pageNumber,
          pageRowCount: records.pageRowCount ?? 25,
          sortByField: records.sortByField,
          sortOrder: records.sortOrder
        };
        return this.#api
          .searchTourPackages({ ...payload, ...paging, searchCurrency, backgroundCheck: true, progressive: true })
          .pipe(
            tap((response) => {
              const current = getState().searchResults.response;
              if (!isCurrent() || !current) return;
              if (response.searchComplete !== false) {
                // R-PT4: the final list, as a first answer of the classic search
                patchState({
                  searchResults:
                    !response.packages?.length && !response.totalCount
                      ? emptyWithDiagnostics(
                          response.providersUnavailable,
                          response.diagnostics ?? current.diagnostics,
                          response.noResult
                        )
                      : new RequestStateModel<TourPackageResultsResponse>(
                          this.#resultsOf(
                            response,
                            current.checks,
                            { ...records, pagingId: response.pagingId },
                            payload,
                            response.diagnostics ?? current.diagnostics
                          )
                        )
                });
                return;
              }
              const takesRows = (current.pagingRecords?.pageNumber ?? 1) <= 1;
              const next = this.#resultsOf(
                response,
                current.checks,
                records,
                payload,
                response.diagnostics ?? current.diagnostics
              );
              setState(
                patch<ToursStateModel>({
                  searchResults: patch<RequestStateModel<TourPackageResultsResponse>>({
                    response: {
                      ...current,
                      ...(takesRows ? { packages: next.packages } : {}),
                      searchComplete: response.searchComplete,
                      diagnostics: next.diagnostics,
                      providersTotal: response.providersTotal,
                      providersAnswered: response.providersAnswered,
                      providersPending: response.providersPending,
                      transfersPending: response.transfersPending,
                      pollAfterMs: response.pollAfterMs,
                      providersUnavailable: response.providersUnavailable,
                      instantConfirmationUnavailable: response.instantConfirmationUnavailable,
                      instantColourBands: response.instantColourBands,
                      pageCount: response.pageCount,
                      totalCount: response.totalCount,
                      pagingRecords: {
                        ...current.pagingRecords,
                        pageCount: response.pageCount,
                        totalCount: response.totalCount
                      }
                    }
                  })
                })
              );
              dispatch(new toursPollSearchProgress(pagingId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS));
            }),
            catchError((e) => {
              if (!isCurrent()) return EMPTY;
              // R-PT6: a search whose coordinator was lost is repeated once, as an expired Phase 2 result is (R-P2-15)
              if (e?.error?.errorCode === 'SearchDataExpired' && !this.#repeatedOnce) {
                this.#repeatedOnce = true;
                patchState({ searchResults: new RequestStateModel<TourPackageResultsResponse>() });
                dispatch(new toursSearchTourPackages(undefined, true));
                return EMPTY;
              }
              // R-PT6: an error the search ended with is the search's error, as the classic answer would show it
              if (e?.status && e.status !== 0 && e.status < 500 && e?.error?.errorCode !== 'SearchDataExpired') {
                patchState({
                  searchResults: new RequestStateModel<TourPackageResultsResponse>(
                    new DataLoadStatusModel('error', searchErrorMessage(e), errorResponseWithTrace(e))
                  )
                });
                return EMPTY;
              }
              if (failures + 1 < PROGRESS_POLL_MAX_FAILURES) {
                dispatch(new toursPollSearchProgress(pagingId, delayMs * 2, failures + 1));
                return EMPTY;
              }
              patchState({
                searchResults: new RequestStateModel<TourPackageResultsResponse>(
                  new DataLoadStatusModel('error', searchErrorMessage(e), errorResponseWithTrace(e))
                )
              });
              return EMPTY;
            })
          );
      })
    );
  }

  @Action(toursSearchChildPackages)
  toursSearchChildPackages(
    { getState, setState }: StateContext<ToursStateModel>,
    { parentHotelCode, reload }: toursSearchChildPackages
  ) {
    const childResults = getState().childResults.find((e) => e.parentHotelCode === parentHotelCode);
    if (childResults?.results?.loadState?.status === 'loading') return;
    if (!reload && childResults?.results?.response?.packages?.length) return; // already loaded

    setState(
      patch<ToursStateModel>({
        childResults: insertItem({
          parentHotelCode,
          results: new RequestStateModel<TourPackageResultsResponse>(new DataLoadStatusModel('loading'))
        })
      })
    );

    const {
      searchResults: { loadState, response: searchResultsResponse },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;

    const payload = deepClean(searchForm.model);

    const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
    return this.#api
      .searchTourPackages({
        ...payload,
        hotelCodes: [parentHotelCode],
        searchCurrency,
        groupByHotel: false,
        groupByHotelId: false,
        pagingId: searchResultsResponse?.pagingId
      })
      .pipe(
        tap((response) => {
          setState(
            patch<ToursStateModel>({
              childResults: updateItem((e) => e.parentHotelCode === parentHotelCode, {
                parentHotelCode,
                results: new RequestStateModel<TourPackageResultsResponse>({
                  ...response,
                  packages: response.packages?.map((e) => ({
                    ...e,
                    hotel: e.hotel
                      ? hotelPackageExtraParamsProcess({ childrenAges: payload.childrenAges ?? [] }, [e.hotel])[0]
                      : undefined
                  }))
                })
              })
            })
          );
        }),
        catchError((e) => {
          setState(
            patch<ToursStateModel>({
              childResults: updateItem((e) => e.parentHotelCode === parentHotelCode, {
                parentHotelCode,
                results: new RequestStateModel<TourPackageResultsResponse>(
                  new DataLoadStatusModel('error', searchErrorMessage(e), errorResponseWithTrace(e))
                )
              })
            })
          );
          return throwError(() => e);
        })
      );
  }

  /* The check of a pressed row (qa.md "Phase 2" row states, R-P2-4, R-P2-6, R-P2-17): its Details answer, shown on the row */

  #checks(getState: StateContext<ToursStateModel>['getState']): TourChecksModel | undefined {
    return getState().searchResults.response?.checks;
  }

  /** Replaces the checks (and, when given, the packages) of the page shown - one patch, so the rows not named keep their identity. */
  #setChecks(setState: StateContext<ToursStateModel>['setState'], checks: TourChecksModel, packages?: TourPackage[]) {
    setState(
      patch<ToursStateModel>({
        searchResults: patch<RequestStateModel<TourPackageResultsResponse>>({
          response: (response) =>
            response
              ? {
                  ...response,
                  checks,
                  ...(packages ? { packages } : {})
                }
              : response
        })
      })
    );
  }

  #pagingOf(getState: StateContext<ToursStateModel>['getState']) {
    const paging = getState().searchResults.response?.pagingRecords;
    return paging?.pagingId
      ? {
          pagingId: paging.pagingId,
          pageNumber: paging.pageNumber ?? 1,
          sortByField: paging.sortByField,
          sortOrder: paging.sortOrder,
          pageRowCount: paging.pageRowCount ?? 25
        }
      : null;
  }

  /** online-api answers `SearchDataExpired` for a result it no longer holds: tell the agent and repeat the search once (never a loop). */
  #onExpired({ dispatch }: StateContext<ToursStateModel>): boolean {
    if (this.#repeatedOnce) return false;
    this.#repeatedOnce = true;
    this.#warn('The search results have expired - the search will be repeated.');
    dispatch(new toursSearchTourPackages(undefined, true));
    return true;
  }

  /** A short warning in the site's language; the services are looked up when needed (the state is built before any screen). */
  #warn(message: string) {
    this.#injector.get(MatSnackBar).open(this.#injector.get(TranslateService).instant(message), '', {
      panelClass: 'snackbar-warning',
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  @Action(toursRowCheckStarted)
  toursRowCheckStarted({ getState, setState }: StateContext<ToursStateModel>, { key }: toursRowCheckStarted) {
    if (!key || !getState().searchResults.response) return;
    const checks = this.#checks(getState);
    const old = checks?.rows[key];
    this.#setChecks(setState, {
      rows: {
        ...(checks?.rows ?? {}),
        [key]: { state: 'checking', ...(old?.shownAnyway ? { shownAnyway: true } : {}) }
      },
      resort: checks?.resort ?? false
    });
  }

  /** R-P2-4: the answer changes its own row only, matched by key; a row the page does not show as pressed (another search) is left alone. */
  @Action(toursRowCheckAnswered)
  toursRowCheckAnswered({ getState, setState }: StateContext<ToursStateModel>, { key, check }: toursRowCheckAnswered) {
    const state = getState();
    const response = state.searchResults.response;
    const old = response?.checks?.rows[key];
    if (!response || !old) return;
    const childrenAges = (deepClean(state.searchForm.model ?? {}) as { childrenAges?: number[] }).childrenAges ?? [];
    const row: TourRowCheckView = {
      ...check,
      ...(check.package?.hotel
        ? {
            package: {
              ...check.package,
              hotel: hotelPackageExtraParamsProcess({ childrenAges }, [check.package.hotel])[0]
            }
          }
        : {}),
      // a row the agent let through stays let through while it is still unavailable; a result of another kind starts clean
      ...(old.shownAnyway && check.state === 'unavailable' ? { shownAnyway: true } : {})
    };
    const packages = response.packages ?? [];
    const checks = withRowCheck(response.checks, key, row, packages, response.pagingRecords ?? {});
    const shown = withCheckedPackages(packages, checks);
    this.#setChecks(setState, checks, shown === packages ? undefined : shown);
    if (row.package)
      setState(
        patch<ToursStateModel>({
          childResults: (list) =>
            list.map((child) =>
              child.results.response?.packages
                ? {
                    ...child,
                    results: {
                      ...child.results,
                      response: {
                        ...child.results.response,
                        packages: withCheckedPackages(child.results.response.packages, checks)
                      }
                    }
                  }
                : child
            )
        })
      );
  }

  @Action(toursDismissTourRow)
  toursDismissTourRow(ctx: StateContext<ToursStateModel>, { key }: toursDismissTourRow) {
    const paging = this.#pagingOf(ctx.getState);
    if (!paging) return;
    return this.#api
      .dismissRow({
        pagingId: paging.pagingId,
        pageNumber: paging.pageNumber,
        key,
        sortByField: paging.sortByField,
        sortOrder: paging.sortOrder,
        pageRowCount: paging.pageRowCount
      })
      .pipe(
        tap((answer) => {
          const state = ctx.getState();
          const response = state.searchResults.response;
          const checks = response?.checks;
          if (!response) return;
          const rows = { ...(checks?.rows ?? {}) };
          delete rows[key];
          ctx.setState(
            patch<ToursStateModel>({
              searchResults: patch<RequestStateModel<TourPackageResultsResponse>>({
                response: (current) =>
                  current
                    ? {
                        ...current,
                        packages: (current.packages ?? []).filter((p) => tourRowKey(p) !== key),
                        totalCount: answer.totalCount,
                        pageCount: answer.pageCount,
                        pagingRecords: {
                          ...current.pagingRecords,
                          totalCount: answer.totalCount,
                          pageCount: answer.pageCount
                        },
                        ...(checks ? { checks: { ...checks, rows } } : {})
                      }
                    : current
              })
            })
          );
        }),
        catchError((e) => {
          if (e?.errorCode === 'SearchDataExpired') this.#onExpired(ctx);
          return EMPTY;
        })
      );
  }

  @Action(toursShowRowAnyway)
  toursShowRowAnyway(ctx: StateContext<ToursStateModel>, { key }: toursShowRowAnyway) {
    const checks = this.#checks(ctx.getState);
    if (!checks?.rows[key]) return;
    this.#setChecks(ctx.setState, {
      ...checks,
      rows: { ...checks.rows, [key]: { ...checks.rows[key], shownAnyway: true } }
    });
  }

  @Action(toursResetSearchResult)
  toursResetSearchResult({ patchState }: StateContext<ToursStateModel>) {
    patchState({
      searchResults: new RequestStateModel<TourPackageResultsResponse>()
    });
  }
  @Action(toursGetAvailableAirlines, { cancelUncompleted: true })
  toursGetAvailableAirlines(
    { getState, setState }: StateContext<ToursStateModel>,
    { refresh, params }: toursGetAvailableAirlines
  ) {
    const {
      searchFormData: { availableAirlines }
    } = getState();

    if (availableAirlines.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Airline[]>) => {
      setState(
        patch<ToursStateModel>({
          searchFormData: patch<ToursSearchFormDataModel>({ availableAirlines: new RequestStateModel(stateParams) })
        })
      );
    };

    params = deepClean(params);

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getAvailableAirlines(params).pipe(
      tap((response) => updateState(response)),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(toursGetArrivalDistricts, { cancelUncompleted: true })
  toursGetArrivalDistricts(
    { getState, setState }: StateContext<ToursStateModel>,
    { refresh, params }: toursGetArrivalDistricts
  ) {
    const {
      searchFormData: { arrivalDistricts }
    } = getState();
    if (arrivalDistricts.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<DistrictsTreeItem[]>) => {
      setState(
        patch<ToursStateModel>({
          searchFormData: patch<ToursSearchFormDataModel>({ arrivalDistricts: new RequestStateModel(stateParams) })
        })
      );
    };

    params = deepClean(params);

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getArrivalDistricts(params).pipe(
      tap((response) => updateState(response)),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(toursGetHotels, { cancelUncompleted: true })
  toursGetHotels({ getState, setState }: StateContext<ToursStateModel>, { refresh, params }: toursGetHotels) {
    const {
      searchFormData: { hotels }
    } = getState();

    if (hotels.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Hotel[]>) => {
      setState(
        patch<ToursStateModel>({
          searchFormData: patch<ToursSearchFormDataModel>({ hotels: new RequestStateModel(stateParams) })
        })
      );
    };

    params = deepClean(params);

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getHotels(params).pipe(
      tap((response) => {
        response.sort((a, b) => {
          const hotelNameA = a?.hotelName?.toUpperCase();
          const hotelNameB = b?.hotelName?.toUpperCase();
          return hotelNameA && hotelNameB ? hotelNameA.localeCompare(hotelNameB) : 0;
        });
        updateState(response);
      }),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(toursLoadFlightDateOptions)
  toursLoadFlightDateOptions(
    { getState, setState }: StateContext<ToursStateModel>,
    { params, identifier }: toursLoadFlightDateOptions
  ) {
    const {
      searchFormData: { flightAvailabilities }
    } = getState();

    const exists = flightAvailabilities.some((e) => e.identifier === identifier);
    if (exists) return;

    const updateState = (data: RequestStateModel<GetFlightDateOptionsResponse>) => {
      setState(
        patch<ToursStateModel>({
          searchFormData: patch<ToursSearchFormDataModel>({
            flightAvailabilities: insertItem<ToursFlightAvailabilityModel>({ data, identifier })
          })
        })
      );
    };

    return this.#catalogueApi.getFlightDateOptions(params).pipe(
      tap((response) => {
        updateState({
          response,
          loadState: new DataLoadStatusModel('completed')
        });
      }),
      catchError((e) => {
        updateState({
          response: null,
          loadState: new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        });
        return throwError(() => e);
      })
    );
  }

  @Action(toursGetDestinationCountries, { cancelUncompleted: true })
  toursGetDestinationCountries(
    { getState, setState }: StateContext<ToursStateModel>,
    { refresh, departureCityUID }: toursGetDestinationCountries
  ) {
    const {
      searchFormData: { destinationCountries }
    } = getState();

    if (destinationCountries.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Country[]>) => {
      setState(
        patch<ToursStateModel>({
          searchFormData: patch<ToursSearchFormDataModel>({ destinationCountries: new RequestStateModel(stateParams) })
        })
      );
    };

    updateState(new DataLoadStatusModel('loading'));

    // DEST-017: departureCityUID narrows the offered countries to what is sold from that city (package mode)
    return this.#catalogueApi.getDestinationCountries(deepClean({ onlyHotels: false, departureCityUID })).pipe(
      tap((response) => {
        setState(
          patch<ToursStateModel>({
            searchFormData: patch<ToursSearchFormDataModel>({
              destinationDefaultCountry: response?.defaultCountryCode ?? null,
              destinationDefaultDepartureCityUID: response?.defaultDepartureCityUID ?? null
            })
          })
        );
        updateState(response?.countries ?? []);
      }),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            e?.displayErrorMessage ??
              "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }
  /* Selectors */

  @Selector([ToursState])
  static availableAirlines({ searchFormData: { availableAirlines } }: ToursStateModel) {
    return availableAirlines;
  }

  static findAirlineName(code: string) {
    return createSelector([ToursState], ({ searchFormData: { availableAirlines } }: ToursStateModel): string | null => {
      if (!availableAirlines?.response?.length) return null;
      return availableAirlines?.response?.find((c) => c.airlineCode === code)?.airlineName ?? null;
    });
  }
  @Selector([ToursState])
  static availableAirlinesWithIcons({ searchFormData: { availableAirlines } }: ToursStateModel) {
    return {
      ...availableAirlines,
      response: availableAirlines.response?.length
        ? // R-FC38 (flight-catalogue qa.md): the airline's logo from NugiosCore, resolved by AirlineLogoIconRegistry
          availableAirlines.response.map((a) => ({ ...a, icon: airlineLogoIcon(a?.airlineCode) }))
        : null
    };
  }

  @Selector([ToursState])
  static destinationCountries({ searchFormData: { destinationCountries } }: ToursStateModel) {
    return destinationCountries;
  }

  @Selector([ToursState])
  static destinationDefaultCountry({ searchFormData: { destinationDefaultCountry } }: ToursStateModel) {
    return destinationDefaultCountry;
  }

  @Selector([ToursState])
  static destinationDefaultDepartureCityUID({
    searchFormData: { destinationDefaultDepartureCityUID }
  }: ToursStateModel) {
    return destinationDefaultDepartureCityUID;
  }

  static findDestinationCountryName(code: string) {
    return createSelector(
      [ToursState],
      ({ searchFormData: { destinationCountries } }: ToursStateModel): string | null => {
        if (!destinationCountries.response?.length) return null;
        return destinationCountries.response.find((c) => c.countryCode === code)?.countryName ?? null;
      }
    );
  }

  static flightAvailabilityByParams(identifier: string) {
    return createSelector([ToursState], ({ searchFormData: { flightAvailabilities } }: ToursStateModel) => {
      const flightAvailability = flightAvailabilities.find((e) => e.identifier === identifier)?.data;
      if (flightAvailability) return flightAvailability;
      else return new RequestStateModel<GetFlightDateOptionsResponse>(new DataLoadStatusModel('loading'));
    });
  }

  static getHotelExtraParams(id: string) {
    return createSelector([ToursState], ({ searchResults: { response } }: ToursStateModel) => {
      return (response?.packages as ITourPackage[])?.find((e) => e.id === id)?.hotel?._extraParams;
    });
  }
  static getChildsOfHotel(parentHotelCode: string) {
    return createSelector([ToursState], (state: ToursStateModel) => {
      return state.childResults.find((e) => e.parentHotelCode === parentHotelCode) ?? null;
    });
  }

  @Selector([ToursState])
  static childResults({ childResults }: ToursStateModel) {
    return childResults;
  }
  @Selector([ToursState])
  static arrivalDistricts({ searchFormData: { arrivalDistricts } }: ToursStateModel) {
    return arrivalDistricts;
  }
  @Selector([ToursState])
  static hotels({ searchFormData: { hotels } }: ToursStateModel) {
    return hotels;
  }

  @Selector([ToursState])
  static isResult({ searchResults }: ToursStateModel) {
    return Boolean(searchResults?.response?.packages?.length);
  }

  @Selector([ToursState])
  static resultIsGroupByHotels({ searchResults: { response } }: ToursStateModel) {
    return Boolean(response?.isGroupByHotels);
  }

  @Selector([ToursState])
  static searchLoadStatus({ searchResults: { loadState } }: ToursStateModel) {
    return loadState ?? null;
  }

  /** R-PT2: the search window stays until the first tours are listed, the search completed (an empty result too) or failed. */
  @Selector([ToursState])
  static searchFirstBatchReady({ searchResults: { loadState, response } }: ToursStateModel): boolean {
    if (loadState.status === 'loading') return false;
    if (loadState.status === 'error') return true;
    return Boolean(response?.packages?.length) || response?.searchComplete !== false;
  }

  @Selector([ToursState])
  static searchResults({ searchResults }: ToursStateModel) {
    return searchResults;
  }
  @Selector([ToursState])
  static searchFormData({ searchForm: { model } }: ToursStateModel) {
    return model;
  }

  @Selector([ToursState])
  static selectedTourOtherPackages({ selectedTourOtherPackages }: ToursStateModel) {
    return { ...selectedTourOtherPackages, response: selectedTourOtherPackages.response?.packages ?? [] };
  }
}
